/** Generic bookmark state + toggle for any entity type. */
import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { handleCourse, handleLesson } from '@/lib/api/update-user-activity';
import { useAuthUser } from '@/lib/auth/common/useSession';

export type BookmarkEntityType = 'course' | 'lesson';

interface UseBookmarkOptions {
  entityType: BookmarkEntityType;
  entityUid?: string;
  courseUid?: string; // required for lessons
  initialBookmarked?: boolean;
  onLoginRequired?: () => void;
}

interface UseBookmarkReturn {
  isBookmarked: boolean;
  isLoading: boolean;
  toggleBookmark: () => Promise<void>;
  setBookmarked: (value: boolean) => void;
}

/** Hook to manage bookmark state for courses and lessons. */
export function useBookmark({
  entityType,
  entityUid,
  courseUid,
  initialBookmarked = false,
  onLoginRequired,
}: UseBookmarkOptions): UseBookmarkReturn {
  const [isBookmarked, setIsBookmarked] = useState(initialBookmarked);
  const [isLoading, setIsLoading] = useState(false);
  // See useAuthUser()'s doc comment for why this must be reactive, not a one-shot read.
  const { isLoggedIn } = useAuthUser();
  const queryClient = useQueryClient();

  // Sync with initial value when it changes (e.g. after data fetch)
  useEffect(() => {
    setIsBookmarked(initialBookmarked);
  }, [initialBookmarked]);

  const toggleBookmark = useCallback(async () => {
    if (!isLoggedIn) {
      onLoginRequired?.();
      return;
    }
    
    if (!entityUid) return;

    const newBookmarkState = !isBookmarked;
    
    // Optimistic update
    setIsBookmarked(newBookmarkState);
    setIsLoading(true);

    try {
      if (entityType === 'lesson' && courseUid) {
        await handleLesson({
          courseEntryUid: courseUid,
          lessonEntryUid: entityUid,
          lessonActivity: {
            isBookmarked: newBookmarkState,
          },
          queryClient,
        });
      } else if (entityType === 'course') {
        await handleCourse({
          courseEntryUid: entityUid,
          courseActivity: {
            isBookmarked: newBookmarkState,
          },
          queryClient,
        });
      }
    } catch (error) {
      setIsBookmarked(!newBookmarkState); // revert on error
    } finally {
      setIsLoading(false);
    }
  }, [isBookmarked, entityType, entityUid, courseUid, isLoggedIn, queryClient, onLoginRequired]);

  return {
    isBookmarked,
    isLoading,
    toggleBookmark,
    setBookmarked: setIsBookmarked,
  };
}

export default useBookmark;
